/**
 * Chrome-less suite viewer authenticated with an Infralytiqs IL token
 * (`apiClient.setIlToken`). Used by the Credentials share landing page and
 * mirrors the Infralytiqs SDK embed toolbar + SuiteReportsView.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, FormControl, IconButton,
  InputLabel, MenuItem, Select, Tooltip, Typography, useTheme,
} from '@mui/material';
import {
  AccessTime, ChevronLeft, ChevronRight, Refresh, ZoomIn, ZoomOut,
} from '@mui/icons-material';
import apiClient from 'utils/ApiClient';
import { TimeRangeDef } from '../Settings/reportDsl';
import { useReportsTimeRange } from '../Reports/hooks/useReportsTimeRange';
import { TimePickerPopover } from '../Logs/components/TimePickerPopover';
import { REPORTS_QUICK_RANGES, REPORTS_CUSTOM_TIME_MAX_MS } from '../Reports/reportsTimeRange';
import {
  reportsToolbarSurfaceSx,
  reportsTimeRangeIconButtonSx,
  reportsTimeRangePickerButtonSx,
  reportsToolbarIconButtonSx,
} from '../Reports/reportsToolbarSurface';
import { resolveReportsSuiteId, resolveSuiteIdByName } from '../reportsDefaultServer';
import { SuiteReportsView } from './SuiteReportsView';

interface ModuleConfig {
  _id: string;
  moduleType: string;
  name: string;
  tenantId?: string;
  siteId?: string;
  defaultReportSuiteId?: string;
  defaultTimeRange?: string;
  reportSuites?: Array<{ _id: string; name: string; description?: string }>;
}

interface SuiteOption {
  _id: string;
  name: string;
  description?: string;
}

const msToChDateTime = (ms: number): string =>
  new Date(ms).toISOString().replace('T', ' ').replace('Z', '').slice(0, 23);

export interface IlTokenSuiteReportsProps {
  server: string;
  reportSuites: string[];
  /** Suite name that should load first when more than one suite is shared. */
  defaultReportSuite?: string;
  /** Bumped by the parent when the IL token is (re)applied. */
  token: string;
}

export const IlTokenSuiteReports: React.FC<IlTokenSuiteReportsProps> = ({
  server,
  reportSuites,
  defaultReportSuite,
  token,
}) => {
  const theme = useTheme();
  const serverParam = (server || '').trim();
  const suitesParam = useMemo(
    () => (reportSuites || []).map((s) => s.trim()).filter(Boolean),
    [reportSuites],
  );
  const suitesParamKey = suitesParam.join('||');

  const [loadingConfigs, setLoadingConfigs] = useState(true);
  const [configError, setConfigError] = useState<string | null>(null);
  const [resolvedConfig, setResolvedConfig] = useState<ModuleConfig | null>(null);
  const [allowedSuites, setAllowedSuites] = useState<SuiteOption[]>([]);
  const [selectedSuiteId, setSelectedSuiteId] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    (async () => {
      setLoadingConfigs(true);
      setConfigError(null);
      setResolvedConfig(null);
      setAllowedSuites([]);
      setSelectedSuiteId('');
      try {
        const res: any = await apiClient.get('/il/settings/module-configs');
        if (cancelled) return;
        const list: ModuleConfig[] = Array.isArray(res?.data)
          ? res.data
          : (res?.data?.moduleConfigurations ?? []);
        const clickhouse = list.filter((c) => c.moduleType === 'ClickHouse');

        if (!serverParam) {
          setConfigError('No ClickHouse server was supplied.');
          return;
        }
        const cfg = clickhouse.find((c) => (c.name || '').toLowerCase() === serverParam.toLowerCase());
        if (!cfg) {
          setConfigError(`The ClickHouse server "${serverParam}" is not available for this share.`);
          return;
        }

        const suites = cfg.reportSuites ?? [];
        const wanted: SuiteOption[] = suitesParam.length > 0
          ? suites.filter((s) => suitesParam.some((w) => w.toLowerCase() === (s.name || '').toLowerCase()))
          : suites;

        if (wanted.length === 0) {
          setConfigError(
            suitesParam.length > 0
              ? `The requested report suite(s) are not available on server "${serverParam}".`
              : `The ClickHouse server "${serverParam}" has no report suites.`,
          );
          return;
        }

        const namedDefaultId = resolveSuiteIdByName(wanted, defaultReportSuite);
        const resolvedDefault = namedDefaultId || resolveReportsSuiteId(
          { _id: cfg._id, reportSuites: wanted, defaultReportSuiteId: cfg.defaultReportSuiteId },
        );
        setResolvedConfig(cfg);
        setAllowedSuites(wanted);
        setSelectedSuiteId(
          resolvedDefault && wanted.some((s) => s._id === resolvedDefault) ? resolvedDefault : wanted[0]._id,
        );
      } catch (err: any) {
        if (!cancelled) setConfigError(err?.message || 'Failed to load report configuration.');
      } finally {
        if (!cancelled) setLoadingConfigs(false);
      }
    })();
    return () => { cancelled = true; };
  }, [serverParam, suitesParamKey, token, defaultReportSuite]); // eslint-disable-line react-hooks/exhaustive-deps

  const scopeKey = resolvedConfig && selectedSuiteId ? `${resolvedConfig._id}::${selectedSuiteId}` : '';
  const timeRange = useReportsTimeRange({
    scopeKey,
    defaultTimeRangeLabel: resolvedConfig?.defaultTimeRange || '',
  });
  const pickerDates = timeRange.getPickerInitialDates();

  const timeRangeOverride: TimeRangeDef = useMemo(() => (
    timeRange.activePreset
      ? { preset: timeRange.activePreset }
      : (() => {
        const { start, end } = timeRange.getTimeRangeMs();
        return { start: msToChDateTime(start), end: msToChDateTime(end) };
      })()
  ), [timeRange.activePreset, timeRange.getTimeRangeMs]);

  if (loadingConfigs) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (configError || !resolvedConfig) {
    return (
      <Box sx={{ pt: 2 }}>
        <Alert severity="warning">{configError || 'Unable to load reports.'}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', mb: 3 }}>
      <Box
        sx={{
          display: 'flex', flexWrap: 'wrap', gap: { xs: 1, md: 2 }, alignItems: 'center',
          mb: 3, p: { xs: 1, md: 1.5 }, ...reportsToolbarSurfaceSx,
        }}
      >
        <FormControl size="small" sx={{ minWidth: 200, flex: { xs: 1, md: '0 1 220px' } }}>
          <InputLabel>ClickHouse Server</InputLabel>
          <Select label="ClickHouse Server" value={resolvedConfig._id} disabled>
            <MenuItem value={resolvedConfig._id}>{resolvedConfig.name}</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 240, flex: 1 }}>
          <InputLabel>Report Suite</InputLabel>
          <Select
            label="Report Suite"
            value={allowedSuites.some((s) => s._id === selectedSuiteId) ? selectedSuiteId : ''}
            onChange={(e) => setSelectedSuiteId(e.target.value as string)}
            disabled={allowedSuites.length === 0}
          >
            {allowedSuites.map((s) => (
              <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Tooltip title="Zoom in time range">
            <span>
              <IconButton
                size="small"
                onClick={timeRange.handleZoomIn}
                disabled={timeRange.timeRangeLabel === REPORTS_QUICK_RANGES[0].label}
                sx={reportsTimeRangeIconButtonSx(theme)}
              >
                <ZoomIn fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Shift time range back">
            <IconButton size="small" onClick={() => timeRange.shiftTime('back')} sx={reportsTimeRangeIconButtonSx(theme)}>
              <ChevronLeft fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            color={theme.palette.mode === 'light' ? undefined : 'secondary'}
            onClick={timeRange.handleTimePickerOpen}
            startIcon={<AccessTime sx={{ fontSize: 16 }} />}
            sx={reportsTimeRangePickerButtonSx(theme)}
          >
            {timeRange.timeRangeLabel}
          </Button>
          <Tooltip title="Shift time range forward">
            <IconButton size="small" onClick={() => timeRange.shiftTime('forward')} sx={reportsTimeRangeIconButtonSx(theme)}>
              <ChevronRight fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom out time range">
            <IconButton size="small" onClick={timeRange.handleZoomOut} sx={reportsTimeRangeIconButtonSx(theme)}>
              <ZoomOut fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Tooltip title="Refresh all panels">
          <IconButton onClick={() => setRefreshKey((k) => k + 1)} sx={reportsToolbarIconButtonSx(theme)} aria-label="Refresh all panels">
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      <TimePickerPopover
        anchorEl={timeRange.anchorEl}
        onClose={() => timeRange.setAnchorEl(null)}
        timeRangeLabel={timeRange.timeRangeLabel}
        onQuickRangeSelect={(r) => {
          const full = REPORTS_QUICK_RANGES.find((q) => q.label === r.label);
          if (full) timeRange.handleQuickRangeSelect(full);
        }}
        onApplyCustomRange={timeRange.handleApplyCustomRange}
        initialFrom={pickerDates.from}
        initialTo={pickerDates.to}
        tzName={timeRange.tzName}
        tzString={timeRange.tzString}
        maxCustomRangeMs={REPORTS_CUSTOM_TIME_MAX_MS}
        quickRanges={REPORTS_QUICK_RANGES}
      />

      {selectedSuiteId ? (
        <SuiteReportsView
          configId={resolvedConfig._id}
          tenantId={resolvedConfig.tenantId || ''}
          siteId={resolvedConfig.siteId || ''}
          suiteId={selectedSuiteId}
          timeRangeOverride={timeRangeOverride}
          refreshKey={refreshKey}
        />
      ) : (
        <Typography variant="body2" color="text.secondary">Select a report suite to begin.</Typography>
      )}
    </Box>
  );
};

export default IlTokenSuiteReports;
